// ============================================================
// v16 BOLT-071 — the Curse of Babel string tables. ONE source for
// both platforms: gen:swift emits BabelStrings.swift from this file
// (the BrandTokens pattern), and a completeness test asserts every
// key exists in every language — a missing key is CI-red.
//
// SCOPE: the SEEKER's round surface. v16 covered the chrome (rail,
// state words, banners, sheet buttons, STOP sign); v17 BOLT-074 adds
// the cockpit eyebrows, the ft/km unit words, and a per-CARD table
// (names for the direction family — Jake's explicit scope override —
// data words, and the sheet's effect copy). The hider's screen, the
// lobby, settings, and the manual stay English. Numbers, times, and
// run codes stay latin everywhere.
// ============================================================
import type { BabelLang, PublicState } from '../protocol';

export const BABEL_LANGS: readonly BabelLang[] = ['ja', 'zh', 'fr', 'sw'] as const;

export const BABEL_KEYS = [
  'seeker',
  'ready',
  'played',
  'running',
  'awaiting',
  'locked',
  'radar',
  'nearest',
  'thermo',
  'photo',
  'airhorn',
  'play',
  'cancel',
  'startWalking',
  'demandPhoto',
  'gettingLocation',
  'found',
  'stop',
  'cardsStillAllowed',
  'cardCast',
  'babelInstruction',
  'tapToDismiss',
  // v17 BOLT-074: the cockpit eyebrows + the distance UNIT words (the
  // digits beside them stay latin).
  'time',
  'zone',
  'ft',
  'km',
] as const;
export type BabelKey = (typeof BABEL_KEYS)[number];

const EN: Record<BabelKey, string> = {
  seeker: 'Seeker',
  ready: 'Ready',
  played: 'Played',
  running: 'Running',
  awaiting: 'Awaiting',
  locked: 'Locked',
  radar: 'RADIUS',
  nearest: 'NEAREST', // v24: the landmark family word (the feature is the data line)
  thermo: 'SLICE',
  photo: 'PHOTO',
  airhorn: 'WHISTLE',
  play: 'Play',
  cancel: 'Cancel',
  startWalking: 'Start walking',
  demandPhoto: 'Demand photo',
  gettingLocation: 'Getting location…',
  found: 'Found!',
  stop: 'STOP',
  cardsStillAllowed: 'You can continue to play cards, but must not move.',
  cardCast: 'Card cast',
  babelInstruction: "Hope you're fluent!",
  tapToDismiss: 'Tap to dismiss',
  time: 'Time',
  zone: 'Zone',
  ft: 'FT',
  km: 'KM',
};

export const BABEL_STRINGS: Record<BabelLang, Record<BabelKey, string>> = {
  ja: {
    seeker: '探索者',
    ready: '準備完了',
    played: '使用済み',
    running: '実行中',
    awaiting: '待機中',
    locked: '封鎖中',
    nearest: '最寄り',
    radar: 'レーダー',
    thermo: '温度計',
    photo: '写真',
    airhorn: 'ホイッスル',
    play: '出す',
    cancel: '取消',
    startWalking: '歩き始める',
    demandPhoto: '写真を要求',
    gettingLocation: '位置情報取得中…',
    found: '発見！',
    stop: '止まれ',
    cardsStillAllowed: 'カードは使えますが、動いてはいけません。',
    cardCast: 'カードが使われた',
    babelInstruction: '流暢だといいね！',
    tapToDismiss: 'タップで閉じる',
    time: '時間',
    zone: '区域',
    ft: 'フィート',
    km: 'キロ',
  },
  zh: {
    seeker: '搜寻者',
    ready: '就绪',
    played: '已使用',
    running: '进行中',
    awaiting: '等待中',
    locked: '已锁定',
    nearest: '最近的',
    radar: '雷达',
    thermo: '温度计',
    photo: '照片',
    airhorn: '哨声',
    play: '出牌',
    cancel: '取消',
    startWalking: '开始行走',
    demandPhoto: '索要照片',
    gettingLocation: '正在定位…',
    found: '找到了！',
    stop: '停',
    cardsStillAllowed: '你可以继续出牌，但不能移动。',
    cardCast: '已打出一张牌',
    babelInstruction: '希望你精通此语！',
    tapToDismiss: '点按关闭',
    time: '时间',
    zone: '区域',
    ft: '英尺',
    km: '公里',
  },
  fr: {
    seeker: 'Chercheur',
    ready: 'Prêt',
    played: 'Jouée',
    running: 'En cours',
    awaiting: 'En attente',
    locked: 'Verrouillé',
    nearest: 'LE PLUS PROCHE',
    radar: 'DÉTECTEUR',
    thermo: 'THERMOMÈTRE',
    photo: 'CLICHÉ',
    airhorn: 'SIFFLET',
    play: 'Jouer',
    cancel: 'Annuler',
    startWalking: 'Commencer à marcher',
    demandPhoto: 'Exiger une photo',
    gettingLocation: 'Localisation…',
    found: 'Trouvé !',
    stop: 'HALTE',
    cardsStillAllowed: 'Vous pouvez continuer à jouer des cartes, mais sans bouger.',
    cardCast: 'Carte jouée',
    babelInstruction: 'J\'espère que vous êtes bilingue !',
    tapToDismiss: 'Toucher pour fermer',
    time: 'DURÉE',
    zone: 'SECTEUR',
    ft: 'PIEDS',
    km: 'KILOM.',
  },
  sw: {
    seeker: 'Mtafutaji',
    ready: 'Tayari',
    played: 'Imechezwa',
    running: 'Inaendelea',
    awaiting: 'Inasubiri',
    locked: 'Imefungwa',
    nearest: 'KARIBU',
    radar: 'RADA',
    thermo: 'KIPIMAJOTO',
    photo: 'PICHA',
    airhorn: 'FILIMBI',
    play: 'Cheza',
    cancel: 'Ghairi',
    startWalking: 'Anza kutembea',
    demandPhoto: 'Dai picha',
    gettingLocation: 'Inatafuta mahali…',
    found: 'Amepatikana!',
    stop: 'SIMAMA',
    cardsStillAllowed: 'Unaweza kuendelea kucheza kadi, lakini usisogee.',
    cardCast: 'Kadi imechezwa',
    babelInstruction: 'Natumai unaifahamu lugha hii!',
    tapToDismiss: 'Gusa kufunga',
    time: 'Muda',
    zone: 'Eneo',
    ft: 'FUTI',
    km: 'KILOMITA',
  },
};

// ============================================================
// v17 BOLT-074 — the per-card sub-table. v16 translated the chrome
// only; Jake's prod look found the holes: the cockpit eyebrows, the
// direction-family card NAMES (his explicit scope override), the card
// sheet's effect copy, and the orange data line. The wire stays frozen
// — the server still sends English `effect`, and the CLIENT swaps it by
// card id. Numbers and codes stay latin everywhere.
// ============================================================

export interface BabelCard {
  /** Card NAME. Only the direction family renders one (every other family
   *  takes its word from the chrome table above). */
  name?: string;
  /** The orange `.qcard__data` word. Radar/thermo distances are composed from
   *  digits + the `ft`/`km` chrome keys instead. */
  data?: string;
  /** The card sheet's effect copy. */
  effect: string;
}

/** The ENGLISH source the four tables are authored against. It is not rendered
 *  (the wire's own `effect` is), but `babel-cards.test.ts` pins it equal to the
 *  server's deckTemplate — so an English edit that leaves the translations
 *  stale is CI-red. The v16 Freeze-before-Babel law, mechanized. */
export const BABEL_CARDS_EN: Record<string, BabelCard> = {
  radar1000: { effect: 'Reveals whether the hider is within 1000 ft of you.' },
  radar500: { effect: 'Reveals whether the hider is within 500 ft of you.' },
  radar250: { effect: 'Reveals whether the hider is within 250 ft of you.' },
  radar100: { effect: 'Reveals whether the hider is within 100 ft of you.' },
  radar1km: { effect: 'Reveals whether the hider is within 1 km of you.' },
  radar5km: { effect: 'Reveals whether the hider is within 5 km of you.' },
  direction: {
    name: 'DIRECTION',
    data: 'X-CUT',
    effect: 'Splits the map into four diagonal slices centered on you. You learn which slice the hider is in.',
  },
  quadrants: {
    name: 'QUADRANTS',
    data: 'CROSS',
    effect: 'Splits the map into four quarters. You learn which quarter the hider is in.',
  },
  borders: {
    name: 'BORDERS',
    data: '25/75',
    effect: 'Tells you whether the hider is near the edge of the play area or in the middle.',
  },
  // v24 BOLT-118..122 — the LANDMARK cards: cut by which real feature the hider
  // is nearest to. `data` is the feature class, which is what the rail shows.
  nearestPark: { name: 'NEAREST PARK', data: 'PARK', effect: 'Which park is the hider closest to?' },
  nearestRestaurant: { name: 'NEAREST RESTAURANT', data: 'FOOD', effect: 'Nearest place to eat.' },
  nearestPlayground: { name: 'NEAREST PLAYGROUND', data: 'PLAYGROUND', effect: 'Which playground is the hider closest to?' },
  nearestSchool: { name: 'NEAREST SCHOOL', data: 'SCHOOL', effect: 'Which school is the hider closest to?' },
  nearestWater: { name: 'NEAREST WATER', data: 'WATER', effect: 'Nearest water.' },
  nearestBridge: { name: 'NEAREST BRIDGE', data: 'BRIDGE', effect: 'Which bridge is the hider closest to?' },
  thermo250: { effect: "Walk at least 250 ft, then learn if you're getting warmer." },
  photoUp: { data: 'UP', effect: 'The hider sends a photo straight up.' },
  photoMailbox: { data: 'MAILBOX', effect: 'The hider sends a photo of the nearest mailbox.' },
  photoHouse: { data: 'HOUSE', effect: 'The hider sends a photo of the closest house.' },
  photoFeet: { data: 'FEET', effect: 'The hider sends a photo of their feet.' },
  photoBuilding: { data: 'TOWER', effect: 'The hider sends a photo of the tallest building in view.' },
  photoSelfie: { data: 'SELFIE', effect: 'The hider sends a selfie. Whatever is behind them comes too.' },
  airhorn: {
    data: 'FULL BLAST',
    effect: "Sounds a piercing whistle on the hider's phone. If it doesn't play out loud, you get the card back.",
  },
};

export const BABEL_CARDS: Record<BabelLang, Record<string, BabelCard>> = {
  ja: {
    radar1000: { effect: '隠れ手が1000フィート以内にいるかどうかが分かります。' },
    radar500: { effect: '隠れ手が500フィート以内にいるかどうかが分かります。' },
    radar250: { effect: '隠れ手が250フィート以内にいるかどうかが分かります。' },
    radar100: { effect: '隠れ手が100フィート以内にいるかどうかが分かります。' },
    radar1km: { effect: '隠れ手が1キロ以内にいるかどうかが分かります。' },
    radar5km: { effect: '隠れ手が5キロ以内にいるかどうかが分かります。' },
    direction: { name: '方角', data: 'X字切り', effect: '地図をあなたを中心に斜めに四分割します。隠れ手がどの区画にいるか分かります。' },
    quadrants: { name: '四分割', data: '十字切り', effect: '地図を四つの区画に分けます。隠れ手がどの区画にいるか分かります。' },
    borders: { name: '境界', data: '25/75', effect: '隠れ手が遊び場の端寄りか、中央寄りかが分かります。' },
    nearestPark: { name: '最寄りの公園', data: '公園', effect: '隠れ手が最も近い公園が分かります。' },
    nearestRestaurant: { name: '最寄りの飲食店', data: '飲食', effect: '最も近い飲食店です。' },
    nearestPlayground: { name: '最寄りの遊び場', data: '遊具', effect: '隠れ手が最も近い遊び場が分かります。' },
    nearestSchool: { name: '最寄りの学校', data: '学校', effect: '隠れ手が最も近い学校が分かります。' },
    nearestWater: { name: '最寄りの水辺', data: '水辺', effect: '最も近い水辺です。' },
    nearestBridge: { name: '最寄りの橋', data: '橋', effect: '隠れ手が最も近い橋が分かります。' },
    thermo250: { effect: '250フィート以上歩くと、近づいているかどうかが分かります。' },
    photoUp: { data: '真上', effect: '隠れ手が真上の写真を送ります。' },
    photoMailbox: { data: '郵便受け', effect: '隠れ手が一番近い郵便受けの写真を送ります。' },
    photoHouse: { data: '家', effect: '隠れ手が一番近い家の写真を送ります。' },
    photoFeet: { data: '足元', effect: '隠れ手が自分の足元の写真を送ります。' },
    photoBuilding: { data: '高い建物', effect: '隠れ手が見える中で一番高い建物の写真を送ります。' },
    photoSelfie: { data: '自撮り', effect: '隠れ手が自撮りを送ります。背景も一緒に写ります。' },
    airhorn: { data: '全開', effect: '隠れ手の端末で甲高いホイッスルが鳴ります。音が出なければカードは戻ります。' },
  },
  zh: {
    radar1000: { effect: '显示躲藏者是否在你1000英尺范围内。' },
    radar500: { effect: '显示躲藏者是否在你500英尺范围内。' },
    radar250: { effect: '显示躲藏者是否在你250英尺范围内。' },
    radar100: { effect: '显示躲藏者是否在你100英尺范围内。' },
    radar1km: { effect: '显示躲藏者是否在你1公里范围内。' },
    radar5km: { effect: '显示躲藏者是否在你5公里范围内。' },
    direction: { name: '方向', data: 'X形切分', effect: '以你为中心把地图斜切成四块。你会知道躲藏者在哪一块。' },
    quadrants: { name: '象限', data: '十字切分', effect: '把地图分成四个象限。你会知道躲藏者在哪一块。' },
    borders: { name: '边界', data: '25/75', effect: '告诉你躲藏者靠近场地边缘还是在中间。' },
    nearestPark: { name: '最近的公园', data: '公园', effect: '告诉你躲藏者最靠近哪个公园。' },
    nearestRestaurant: { name: '最近的餐馆', data: '餐饮', effect: '最近的用餐地点。' },
    nearestPlayground: { name: '最近的游乐场', data: '游乐', effect: '告诉你躲藏者最靠近哪个游乐场。' },
    nearestSchool: { name: '最近的学校', data: '学校', effect: '告诉你躲藏者最靠近哪所学校。' },
    nearestWater: { name: '最近的水域', data: '水域', effect: '最近的水域。' },
    nearestBridge: { name: '最近的桥', data: '桥梁', effect: '告诉你躲藏者最靠近哪座桥。' },
    thermo250: { effect: '至少走250英尺，然后得知你是否越来越近。' },
    photoUp: { data: '向上', effect: '躲藏者会发来一张向上拍的照片。' },
    photoMailbox: { data: '邮箱', effect: '躲藏者会发来最近邮箱的照片。' },
    photoHouse: { data: '房屋', effect: '躲藏者会发来最近房屋的照片。' },
    photoFeet: { data: '双脚', effect: '躲藏者会发来自己双脚的照片。' },
    photoBuilding: { data: '高楼', effect: '躲藏者会发来视野中最高建筑的照片。' },
    photoSelfie: { data: '自拍', effect: '躲藏者会发来一张自拍，背景也会一起入镜。' },
    airhorn: { data: '尖锐哨声', effect: '躲藏者的手机会发出尖锐哨声。若没有响出声，这张牌会退还给你。' },
  },
  fr: {
    radar1000: { effect: 'Indique si le fuyard se trouve à moins de 1000 pieds de vous.' },
    radar500: { effect: 'Indique si le fuyard se trouve à moins de 500 pieds de vous.' },
    radar250: { effect: 'Indique si le fuyard se trouve à moins de 250 pieds de vous.' },
    radar100: { effect: 'Indique si le fuyard se trouve à moins de 100 pieds de vous.' },
    radar1km: { effect: 'Indique si le fuyard se trouve à moins de 1 kilomètre de vous.' },
    radar5km: { effect: 'Indique si le fuyard se trouve à moins de 5 kilomètres de vous.' },
    direction: {
      name: 'CAP',
      data: 'COUPE EN X',
      effect: 'Découpe la carte en quatre tranches diagonales centrées sur vous. Vous apprenez dans laquelle se cache le fuyard.',
    },
    quadrants: {
      name: 'QUARTS',
      data: 'EN CROIX',
      effect: 'Découpe la carte en quatre quarts. Vous apprenez dans lequel se cache le fuyard.',
    },
    borders: {
      name: 'BORDURES',
      data: '25/75',
      effect: 'Vous dit si le fuyard est près du bord du terrain ou plutôt au milieu.',
    },
    nearestPark: { name: 'PARC LE PLUS PROCHE', data: 'PARC', effect: 'De quel parc le fuyard est-il le plus proche ?' },
    nearestRestaurant: { name: 'RESTAURANT LE PLUS PROCHE', data: 'RESTO', effect: 'Le point de restauration le plus proche.' },
    nearestPlayground: { name: 'AIRE DE JEUX LA PLUS PROCHE', data: 'JEUX', effect: "De quelle aire de jeux le fuyard est-il le plus proche ?" },
    nearestSchool: { name: 'ÉCOLE LA PLUS PROCHE', data: 'ÉCOLE', effect: 'De quelle école le fuyard est-il le plus proche ?' },
    nearestWater: { name: "POINT D'EAU LE PLUS PROCHE", data: 'EAU', effect: "Le point d'eau le plus proche." },
    nearestBridge: { name: 'PONT LE PLUS PROCHE', data: 'PONT', effect: 'De quel pont le fuyard est-il le plus proche ?' },
    thermo250: { effect: 'Marchez au moins 250 pieds, puis découvrez si vous vous rapprochez.' },
    photoUp: { data: 'VERS LE HAUT', effect: 'Le fuyard envoie une photo prise vers le haut.' },
    photoMailbox: { data: 'BOÎTE AUX LETTRES', effect: 'Le fuyard envoie une photo de la boîte aux lettres la plus proche.' },
    photoHouse: { data: 'MAISON', effect: 'Le fuyard envoie une photo de la maison la plus proche.' },
    photoFeet: { data: 'PIEDS', effect: 'Le fuyard envoie une photo de ses pieds.' },
    photoBuilding: { data: 'TOUR', effect: 'Le fuyard envoie une photo du plus haut bâtiment en vue.' },
    photoSelfie: { data: 'AUTOPORTRAIT', effect: 'Le fuyard envoie un autoportrait. Tout ce qui se trouve derrière lui vient avec.' },
    airhorn: {
      data: 'PLEIN VOLUME',
      effect: "Un sifflet strident retentit sur le téléphone du fuyard. S'il ne sonne pas, la carte vous revient.",
    },
  },
  sw: {
    radar1000: { effect: 'Inaonyesha kama mfichaji yuko ndani ya futi 1000 kutoka kwako.' },
    radar500: { effect: 'Inaonyesha kama mfichaji yuko ndani ya futi 500 kutoka kwako.' },
    radar250: { effect: 'Inaonyesha kama mfichaji yuko ndani ya futi 250 kutoka kwako.' },
    radar100: { effect: 'Inaonyesha kama mfichaji yuko ndani ya futi 100 kutoka kwako.' },
    radar1km: { effect: 'Inaonyesha kama mfichaji yuko ndani ya kilomita 1 kutoka kwako.' },
    radar5km: { effect: 'Inaonyesha kama mfichaji yuko ndani ya kilomita 5 kutoka kwako.' },
    direction: {
      name: 'MWELEKEO',
      data: 'MKATO X',
      effect: 'Inagawa ramani vipande vinne vya mshazari vikikuzunguka. Utajua mfichaji yuko kipande gani.',
    },
    quadrants: { name: 'ROBO', data: 'MSALABA', effect: 'Inagawa ramani robo nne. Utajua mfichaji yuko robo gani.' },
    borders: { name: 'MIPAKA', data: '25/75', effect: 'Inakuambia kama mfichaji yuko karibu na ukingo wa eneo au katikati.' },
    nearestPark: { name: 'BUSTANI ILIYO KARIBU', data: 'BUSTANI', effect: 'Ni bustani gani iliyo karibu zaidi na anayejificha?' },
    nearestRestaurant: { name: 'MGAHAWA ULIO KARIBU', data: 'CHAKULA', effect: 'Mahali pa karibu zaidi pa kula.' },
    nearestPlayground: { name: 'UWANJA WA MICHEZO ULIO KARIBU', data: 'MICHEZO', effect: 'Ni uwanja gani wa michezo ulio karibu zaidi na anayejificha?' },
    nearestSchool: { name: 'SHULE ILIYO KARIBU', data: 'SHULE', effect: 'Ni shule gani iliyo karibu zaidi na anayejificha?' },
    nearestWater: { name: 'MAJI YALIYO KARIBU', data: 'MAJI', effect: 'Maji yaliyo karibu zaidi.' },
    nearestBridge: { name: 'DARAJA LILILO KARIBU', data: 'DARAJA', effect: 'Ni daraja gani lililo karibu zaidi na anayejificha?' },
    thermo250: { effect: 'Tembea angalau futi 250, kisha ujue kama unakaribia.' },
    photoUp: { data: 'JUU', effect: 'Mfichaji anatuma picha ya juu.' },
    photoMailbox: { data: 'SANDUKU', effect: 'Mfichaji anatuma picha ya sanduku la barua lililo karibu.' },
    photoHouse: { data: 'NYUMBA', effect: 'Mfichaji anatuma picha ya nyumba iliyo karibu.' },
    photoFeet: { data: 'MIGUU', effect: 'Mfichaji anatuma picha ya miguu yake.' },
    photoBuilding: { data: 'MNARA', effect: 'Mfichaji anatuma picha ya jengo refu zaidi analoliona.' },
    photoSelfie: { data: 'PICHA BINAFSI', effect: 'Mfichaji anatuma picha binafsi. Kilicho nyuma yake kinakuja pia.' },
    airhorn: { data: 'FILIMBI KALI', effect: 'Filimbi kali inalia kwenye simu ya mfichaji. Isipolia, unarudishiwa kadi.' },
  },
};

/** One card field, translated — English (the caller's fallback) when no curse
 *  is up, the card is unknown (a community card), or the field is absent. */
export function btCard(lang: BabelLang | null, id: string, field: keyof BabelCard, fallback: string): string {
  return (lang && BABEL_CARDS[lang]?.[id]?.[field]) || fallback;
}

/** The seeker's active Babel language, if any (the hider stays readable). */
export function babelLang(state: PublicState | null): BabelLang | null {
  if (!state || state.you.role !== 'seeker') return null;
  const c = state.activeCurse;
  return c?.kind === 'babel' && c.lang ? c.lang : null;
}

/** Translate a chrome key — English when no curse (or a missing key). */
export function bt(lang: BabelLang | null, key: BabelKey): string {
  return (lang && BABEL_STRINGS[lang]?.[key]) || EN[key];
}
